import type { ComponentProps } from 'react';
import { ArrowUpRight } from 'lucide-react';
import type { WebOpportunity } from '../../../lib/opportunities';
import { useOpportunities, useWorkspaceOpportunities } from '../../../lib/opportunities';
import { useAgentName } from '../../../lib/use-agent-name';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { useTranslation } from '../../../lib/i18n';
import { useWorkspaces } from '../../../lib/workspaces';
import { Card, TextButton, Typography } from '../ui';
import styles from './WorkspaceOpportunities.module.css';
import { useCategoryRedesign } from '../../../lib/use-category-redesign';
import { WorkspaceOpportunities as WorkspaceOpportunitiesLegacy } from './WorkspaceOpportunities.legacy';

// "Suggested goals ({count})" — the bottom section of the workspace Goals tab (Figma 10666:89977 /
// 10666:90268, whose heading reads "Suggested goals"). These are the OPPORTUNITY goals an agent proposed
// for THIS workspace: outcomes it spotted that aren't goals yet, each with a "Talk to {agent}" CTA that
// hands the proposal to the side chat so the user can decide to pursue it.
//
// Written-down exception to native parity: native's own heading for this same data is "Opportunities"
// (`opportunity.section.title`). Web intentionally uses the Figma web frame's wording instead — the
// underlying entity/type stay named "opportunity" throughout the code, only the on-screen heading differs.
//
// Same data the native apps show in their Growth-tab `OpportunitiesDeck` and the home pile shows across all
// workspaces — one unscoped `/api/opportunities` read, narrowed to this workspace. Above this section the
// tab lists the LIVE goals (KICKOFF/ACTIVE/ACHIEVED), so the two never overlap: a proposal appears here
// until it becomes a goal, then it appears in the grid above instead.
//
// Renders nothing at all when the workspace has no proposals (or while they load), so the tab never shows a
// section header over an empty grid.
//
// `workspaceId` omitted (the top-level Goals screen) reads every proposal across every Space, off the SAME
// unscoped `/api/opportunities` query the scoped read narrows — one cached response, never two.
function WorkspaceOpportunitiesRedesign({ workspaceId, page }: { workspaceId?: string; page: string }) {
  const { t } = useTranslation();
  const scoped = useWorkspaceOpportunities(workspaceId);
  const all = useOpportunities();
  const opportunities = workspaceId ? scoped : all;

  if (!opportunities || opportunities.length === 0) {
    return null;
  }

  return (
    <section className={styles.section}>
      <h2 className={styles.title}>{t('categoryView.goals.suggestedTitle', { count: opportunities.length })}</h2>
      <div className={styles.grid}>
        {opportunities.map((opportunity) => (
          <OpportunityCard key={opportunity.id} opportunity={opportunity} page={page} />
        ))}
      </div>
    </section>
  );
}

function OpportunityCard({ opportunity, page }: { opportunity: WebOpportunity; page: string }) {
  const { t } = useTranslation();
  const agent = useAgentName();
  const openSideChat = useOpenSideChat();
  const { logPageClickBi } = useGenericBi();
  const workspaces = useWorkspaces();
  // Which Space the suggestion is for, over its title (Figma 21055:254336).
  const spaceName = workspaces.status === 'ready' ? workspaces.workspaces.find((w) => w.id === opportunity.workspaceId)?.name : undefined;

  const talk = () => {
    logPageClickBi({ page_name: page, button_name: 'opportunity_talk', answer: opportunity.id });
    // The same seed the home pile sends, so pursuing a proposal reads identically from either surface.
    openSideChat({ kind: 'new', message: t('home.opportunities.chatPrompt', { title: opportunity.title }) });
  };

  return (
    <Card className={styles.card}>
      {spaceName && (
        <Typography variant="heading-04" color="primary" component="span">
          {spaceName}
        </Typography>
      )}
      <h3 className={styles.cardTitle}>{opportunity.title || t('goals.untitled')}</h3>
      {/* Design QA (Monday 12772425600, Figma 10666-90078): the card's CTA is the DS **Text button** with a
          trailing ↗ — an 18px line of words at the card's bottom edge, not a 36px filled box. `TextButton`,
          not `Button variant="text"`: the ghost Type is still a real box with padding and a hover fill, which
          is the mistake the WhatsApp invite rows already made (see orion-web/CLAUDE.md → "Buttons"). This is
          the same CTA the sibling opportunity surfaces (`catch-up/OpportunitiesModal`, `OpportunitiesExpanded`,
          `PiledUpJobs`) and native's expanded-list `OpportunityCard` already draw. */}
      <TextButton size="small" className={styles.cta} endIcon={<ArrowUpRight size={16} className="dir-flip" aria-hidden />} onClick={talk}>
        {t('goals.talk', { agent: agent.short })}
      </TextButton>
    </Card>
  );
}

/** The redesign rides the activity-map home's release (`lib/use-category-redesign.ts`); off, this is the legacy design. */
export function WorkspaceOpportunities(props: ComponentProps<typeof WorkspaceOpportunitiesRedesign>) {
  const redesign = useCategoryRedesign();
  if (!redesign.resolved) return null;
  return redesign.on ? <WorkspaceOpportunitiesRedesign {...props} /> : <WorkspaceOpportunitiesLegacy {...props} />;
}
